import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  CompanyUsersTab,
  CurrentUsageTab,
  ExportUsersButton,
  UsageHistoryDetailTab,
  UsageHistoryTab,
} from 'components/CompanyDetail';
import { PageContainer, PageHeader } from 'components/ds';
import { ChevronLeft, Plus } from 'lucide-react';
import {
  useOrganizationUsageData,
  useOrganizationUsers,
  useUpdateOrganizationUser,
} from 'queries/useOrganization';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Organization } from 'services/organization';

/**
 * Full-PAGE org usage & members for super-admins — the routed detail view reached from the
 * Organizations list "View usage" action (mockup: onNavigate('company-usage'), NOT a modal).
 * Same tabs/data as the legacy CompanyDetailModal, rendered as a DS page with a back link.
 * The org is taken from router state when navigated; falls back to the usage payload on refresh.
 */
const CompanyDetailPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ company?: Organization }>();
  const stateCompany = location.state?.company;

  const [activeTab, setActiveTab] = useState(0);
  const [isEditingUser, setIsEditingUser] = useState(false);

  const { data: usageData, isLoading: isLoadingUsage } = useOrganizationUsageData(id);
  const { data: users, isLoading: isLoadingUsers } = useOrganizationUsers(id);

  const updateUserMutation = useUpdateOrganizationUser();
  const handleUpdateUser = async (userId: string, userData: any) => {
    const currentUser = users?.find((u: any) => u.id === userId);
    if (!currentUser) throw new Error('User not found');
    const result = await updateUserMutation.mutateAsync({
      orgId: id,
      userId,
      userData: {
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        email: currentUser.email,
        locale: userData.locale || currentUser.locale || 'en',
      },
    });
    if (result && result.success === false) {
      throw new Error(result.message || 'User update failed');
    }
  };

  const companyName = stateCompany?.name || (usageData as any)?.name || t('route.managementSystem');
  const limitation = stateCompany?.limitation || (usageData as any)?.limitation;
  const userCount =
    typeof stateCompany?.user_count === 'number' ? stateCompany.user_count : users?.length;

  const expiry =
    limitation?.expiration && limitation.expiration !== 'Unlimited'
      ? new Date(limitation.expiration).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : null;
  const subtitle =
    [
      typeof userCount === 'number'
        ? t('managementSystem.companyDetail.accountsCount', { count: userCount })
        : null,
      expiry ? `${t('managementSystem.companyDetail.expirationDate')}: ${expiry}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || undefined;

  const TAB_LABELS = [
    'managementSystem.companyDetail.currentUsage',
    'managementSystem.companyDetail.usageHistory',
    'managementSystem.companyDetail.usageHistoryDetail',
    'managementSystem.companyDetail.companyUsers',
  ];

  return (
    <PageContainer>
      <Button
        alignSelf='flex-start'
        mb={2}
        color='text.muted'
        fontWeight={500}
        leftIcon={<ChevronLeft size={16} />}
        onClick={() => history.push('/management-system')}
        size='ds-sm'
        variant='ds-ghost'
      >
        {t('managementSystem.allOrganizations', { defaultValue: 'All organizations' })}
      </Button>

      <PageHeader
        title={companyName}
        subtitle={subtitle}
        action={
          <Badge display='flex' variant='ds-success'>
            <HStack spacing={1}>
              <Box w='6px' h='6px' bg='state.success' borderRadius='full' />
              <Text>{t('managementSystem.companyDetail.activeStatus')}</Text>
            </HStack>
          </Badge>
        }
      />

      {isLoadingUsage || isLoadingUsers ? (
        <Center py={20}>
          <Spinner color='primary.solid' size='xl' />
        </Center>
      ) : (
        <Tabs index={activeTab} onChange={setActiveTab} variant='unstyled'>
          <TabList gap={1} mb={5} borderColor='border.default' borderBottomWidth='1px'>
            {TAB_LABELS.map((key) => (
              <Tab
                key={key}
                mb='-1px'
                px={3}
                py={3}
                color='text.muted'
                fontSize='sm'
                fontWeight={500}
                borderColor='transparent'
                borderBottomWidth='2px'
                _hover={{ color: 'text.default' }}
                _selected={{ color: 'primary.solid', borderColor: 'primary.solid' }}
              >
                {t(key)}
              </Tab>
            ))}
          </TabList>

          {activeTab === 3 && (
            <Flex align='center' justify='space-between' pb={4}>
              <Text color='text.muted' fontSize='sm' fontWeight={500}>
                {t('managementSystem.companyDetail.membersCount', {
                  count: users?.length || 0,
                  defaultValue: '{{count}} members',
                })}
              </Text>
              <HStack spacing={2}>
                <ExportUsersButton org={id} isDisabled={isEditingUser} />
                <Button
                  isDisabled={isEditingUser}
                  leftIcon={<Icon as={Plus} boxSize='14px' />}
                  onClick={() => window.dispatchEvent(new CustomEvent('triggerAddUser'))}
                  size='ds-sm'
                  variant='ds-primary'
                >
                  {t('managementSystem.companyDetail.addUser')}
                </Button>
              </HStack>
            </Flex>
          )}

          <TabPanels>
            <TabPanel px={0} pt={0} pb={4}>
              <CurrentUsageTab usageData={usageData} limitation={limitation} />
            </TabPanel>
            <TabPanel px={0} pt={0} pb={4}>
              <UsageHistoryTab usageData={usageData} />
            </TabPanel>
            <TabPanel px={0} pt={0} pb={4}>
              <UsageHistoryDetailTab usageData={usageData} />
            </TabPanel>
            <TabPanel px={0} pt={0} pb={4}>
              <CompanyUsersTab
                users={users}
                organizationId={id}
                onUpdateUser={handleUpdateUser}
                onEditingStateChange={setIsEditingUser}
                isNotSuperAdmin={false}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </PageContainer>
  );
};

export default CompanyDetailPage;
